// Org-structure űrlap-segédek (nem-komponens): form-értékek típusa, alapértékek, a @atlas/contracts
// write-sémái (a resolverhez) és a lokalizált zod-hibatérképek. Külön fájl a komponensektől, hogy a
// react-refresh (Fast Refresh) tiszta maradjon (a *-form-sheet.tsx CSAK a komponenst exportálja).
// A minta az assets/clients form.ts — a validálás forrás-igazsága a CONTRACT (NEM saját séma):
// a resolver a contract write-sémáján fut, a hiba-pathok 1:1 a form-mezők nevei → közvetlenül a mezőre
// terelődnek. A wire-body-t a `to*Body` normalizálja (üres string → null; a slug generált, nem form-mező).
import {
  DepartmentWriteBodySchema,
  PositionWriteBodySchema,
  TeamWriteBodySchema,
} from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { z } from 'zod';

// ── Részleg (department) ────────────────────────────────────────────────────

/** A részleg-form mező-értékei. SZÍN NINCS: az a csapat szintjén él (`TeamFormValues.color`). */
export interface DepartmentFormValues {
  nameHu: string;
  nameEn: string;
  descriptionHu: string;
  descriptionEn: string;
}

export const EMPTY_DEPARTMENT_FORM: DepartmentFormValues = {
  nameHu: '',
  nameEn: '',
  descriptionHu: '',
  descriptionEn: '',
};

type DepartmentWriteBody = z.infer<typeof DepartmentWriteBodySchema>;

/** Form-értékek → wire-body: '' → null a leírás-mezőkön. */
export function toDepartmentBody(v: DepartmentFormValues): DepartmentWriteBody {
  const emptyToNull = (s: string): string | null => (s.trim() === '' ? null : s);
  return {
    nameHu: v.nameHu,
    nameEn: v.nameEn,
    descriptionHu: emptyToNull(v.descriptionHu),
    descriptionEn: emptyToNull(v.descriptionEn),
  };
}

// ── Csapat (team) ────────────────────────────────────────────────────────────

/**
 * A csapat-form mező-értékei. A `color` a MORGENS-paletta nevesített tokenje (a rendszer egyetlen
 * szervezeti szín-forrása); a `departmentId` KÖTELEZŐ uuid — csapat nem létezhet részleg nélkül.
 */
export interface TeamFormValues {
  nameHu: string;
  nameEn: string;
  color: string;
  departmentId: string;
  descriptionHu: string;
  descriptionEn: string;
  /** A csapatvezető identity-id-ja (#117) — üres → nincs kijelölve. */
  leadIdentityId: string;
}

/** Üres (create) alapértékek — a `color` a paletta defaultja (gray). */
export const EMPTY_TEAM_FORM: TeamFormValues = {
  nameHu: '',
  nameEn: '',
  color: 'gray',
  departmentId: '',
  descriptionHu: '',
  descriptionEn: '',
  leadIdentityId: '',
};

type TeamWriteBody = z.infer<typeof TeamWriteBodySchema>;

export function toTeamBody(v: TeamFormValues): TeamWriteBody {
  const emptyToNull = (s: string): string | null => (s.trim() === '' ? null : s);
  return {
    nameHu: v.nameHu,
    nameEn: v.nameEn,
    color: v.color,
    departmentId: v.departmentId,
    descriptionHu: emptyToNull(v.descriptionHu),
    descriptionEn: emptyToNull(v.descriptionEn),
    leadIdentityId: emptyToNull(v.leadIdentityId),
  };
}

// ── Beosztás (position) ──────────────────────────────────────────────────────

/**
 * A beosztás-form mező-értékei (string-alapú; a `teamId` KÖTELEZŐ uuid — üres → mező-hiba).
 * RÉSZLEG-mező NINCS: a beosztás részlege a csapatából következik (egy igazság-forrás).
 */
export interface PositionFormValues {
  nameHu: string;
  nameEn: string;
  teamId: string;
  descriptionHu: string;
  descriptionEn: string;
}

export const EMPTY_POSITION_FORM: PositionFormValues = {
  nameHu: '',
  nameEn: '',
  teamId: '',
  descriptionHu: '',
  descriptionEn: '',
};

type PositionWriteBody = z.infer<typeof PositionWriteBodySchema>;

export function toPositionBody(v: PositionFormValues): PositionWriteBody {
  const emptyToNull = (s: string): string | null => (s.trim() === '' ? null : s);
  return {
    nameHu: v.nameHu,
    nameEn: v.nameEn,
    teamId: v.teamId,
    descriptionHu: emptyToNull(v.descriptionHu),
    descriptionEn: emptyToNull(v.descriptionEn),
  };
}

// ── Lokalizált zod-hibatérképek + resolverek ─────────────────────────────────

/** A kétnyelvű név-mezőpár közös hibatérkép-ága (required / max) — a `validation:org_structure.*` réteg.
 *  Nem-név issue vagy nem-név/max kód → null (a hívó tovább dönt). */
function nameMessage(t: TFunction, code: z.ZodIssueCode): string | null {
  if (code === z.ZodIssueCode.too_small || code === z.ZodIssueCode.invalid_type) {
    return t('validation:org_structure.name.required');
  }
  if (code === z.ZodIssueCode.too_big) return t('validation:org_structure.name.max');
  return null;
}

/** Részleg-hibatérkép: a név-mezők required/max; egyéb org-mező → generikus; más → zod-default. */
export function buildDepartmentErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'nameHu' || field === 'nameEn') {
      const m = nameMessage(t, issue.code);
      if (m) return { message: m };
    }
    if (typeof field === 'string' && field in EMPTY_DEPARTMENT_FORM) {
      return { message: t('validation:org_structure.generic') };
    }
    return { message: ctx.defaultError };
  };
}

/** Csapat-hibatérkép: a név-mezők required/max; a `departmentId` required (üres/nem-uuid); egyéb → generikus. */
export function buildTeamErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'nameHu' || field === 'nameEn') {
      const m = nameMessage(t, issue.code);
      if (m) return { message: m };
    }
    if (field === 'departmentId') {
      return { message: t('validation:org_structure.department.required') };
    }
    if (typeof field === 'string' && field in EMPTY_TEAM_FORM) {
      return { message: t('validation:org_structure.generic') };
    }
    return { message: ctx.defaultError };
  };
}

/** Beosztás-hibatérkép: a név-mezők required/max; a `teamId` required (üres/nem-uuid); egyéb → generikus. */
export function buildPositionErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'nameHu' || field === 'nameEn') {
      const m = nameMessage(t, issue.code);
      if (m) return { message: m };
    }
    if (field === 'teamId') {
      return { message: t('validation:org_structure.team.required') };
    }
    if (typeof field === 'string' && field in EMPTY_POSITION_FORM) {
      return { message: t('validation:org_structure.generic') };
    }
    return { message: ctx.defaultError };
  };
}

/**
 * RHF-resolver a contract-sémából: a validálás a NYERS (string-alapú) form-értékeken fut — a contract
 * preprocess-ei az üres stringet a leírás-mezőn null-ra, a color üreset a defaultra alakítják, így a
 * form-értékek közvetlenül átmennek. A hiba-pathok 1:1 a form-mezők nevei → a mezőre terelődnek. Sikerkor
 * az EREDETI form-értékek mennek tovább (a wire-body-t a mutáció építi a `to*Body`-val — az assets minta).
 */
export function buildDepartmentResolver(t: TFunction): Resolver<DepartmentFormValues> {
  const base = zodResolver(DepartmentWriteBodySchema, {
    errorMap: buildDepartmentErrorMap(t),
  }) as unknown as Resolver<DepartmentFormValues>;
  return async (values, context, options) => {
    const result = await base(values, context, options);
    if (Object.keys(result.errors).length > 0) return { values: {}, errors: result.errors };
    return { values, errors: {} };
  };
}

export function buildTeamResolver(t: TFunction): Resolver<TeamFormValues> {
  const base = zodResolver(TeamWriteBodySchema, {
    errorMap: buildTeamErrorMap(t),
  }) as unknown as Resolver<TeamFormValues>;
  return async (values, context, options) => {
    const result = await base(values, context, options);
    if (Object.keys(result.errors).length > 0) return { values: {}, errors: result.errors };
    return { values, errors: {} };
  };
}

export function buildPositionResolver(t: TFunction): Resolver<PositionFormValues> {
  const base = zodResolver(PositionWriteBodySchema, {
    errorMap: buildPositionErrorMap(t),
  }) as unknown as Resolver<PositionFormValues>;
  return async (values, context, options) => {
    const result = await base(values, context, options);
    if (Object.keys(result.errors).length > 0) return { values: {}, errors: result.errors };
    return { values, errors: {} };
  };
}
